import {
  FileExtension,
  FileMimeType,
} from '@flowx/designer/modules/ai-builder/ai-file-upload.types'
import {
  AttachmentType,
  type ComposerFile,
  type ComposerFileUploadEvent,
  type FilePickResult,
  type FileRefusal,
  FileRefusalReason,
  type FilesInConversation,
} from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import type {
  ConversationFile,
  HarnessAttachmentRef,
  HarnessAttachmentsPolicy,
  HarnessSentFile,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import { COMPOSER_FILE_TEXTS } from '@flowx/designer/modules/ai-builder/utils/composer-files.dictionary'

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const

/** The type and the word for each mime the harness admits. */
export const MIME_DISPLAY: Readonly<Record<string, { type: AttachmentType; label: string }>> = {
  [FileMimeType.PDF]: { type: AttachmentType.Pdf, label: 'PDF' },
  [FileMimeType.DOCX]: { type: AttachmentType.Doc, label: 'DOCX' },
  [FileMimeType.PNG]: { type: AttachmentType.Image, label: 'PNG' },
  [FileMimeType.JPEG]: { type: AttachmentType.Image, label: 'JPG' },
  [FileMimeType.WEBP]: { type: AttachmentType.Image, label: 'WebP' },
  [FileMimeType.BPMN]: { type: AttachmentType.Bpmn, label: 'BPMN' },
}

/** The mime each admitted type's name stands for. */
const EXTENSION_MIME: Readonly<Record<string, FileMimeType>> = {
  [FileExtension.PDF]: FileMimeType.PDF,
  [FileExtension.DOCX]: FileMimeType.DOCX,
  [FileExtension.PNG]: FileMimeType.PNG,
  [FileExtension.JPG]: FileMimeType.JPEG,
  [FileExtension.JPEG]: FileMimeType.JPEG,
  [FileExtension.WEBP]: FileMimeType.WEBP,
  [FileExtension.BPMN]: FileMimeType.BPMN,
}

/** The picker's `accept`: the policy's types, and `.bpmn` by name — browsers report a BPMN file
 * as `''` or `text/xml`, never as its own mime. */
export function acceptOf(policy: HarnessAttachmentsPolicy): string {
  const mimes = Object.keys(policy.types)

  return [...mimes, ...(mimes.includes(FileMimeType.BPMN) ? [FileExtension.BPMN] : [])].join(',')
}

/** The admitted mime a file is sent as, or null when the policy admits none for it: the one the
 * browser reported, else the one its name says — browsers report a BPMN file as `''` or
 * `text/xml`, and an OS without the app for a type reports `''` or `application/octet-stream`. */
export function mimeOf(file: File, policy: HarnessAttachmentsPolicy): string | null {
  const named = EXTENSION_MIME[file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '']

  return [file.type, named].find((mime) => !!mime && mime in policy.types) ?? null
}

/** A size in decimal units, as the policy's limits and the maker's file browser count them:
 * "312 KB", "24.3 MB". One decimal at most, none when it is whole. */
export function fileSize(bytes: number, decimals = 1): string {
  const exponent =
    bytes > 0 ? Math.min(Math.floor(Math.log10(bytes) / 3), SIZE_UNITS.length - 1) : 0
  const value = Math.round((bytes / 1000 ** exponent) * 10 ** decimals) / 10 ** decimals

  // 999 999 B rounds to 1000 KB: say it in the next unit, as 1 MB.
  return value >= 1000 && exponent < SIZE_UNITS.length - 1
    ? fileSize(1000 ** (exponent + 1), decimals)
    : `${value} ${SIZE_UNITS[exponent]}`
}

/** Why a file is too large, as the spec words it: "18 MB, the maximum for a PDF is 15 MB". A file
 * just over the limit shows the decimals that tell it from the limit. */
function sizeOverLimit(bytes: number, limit: number, mime: string): string {
  const maximum = fileSize(limit)
  const decimals = [1, 2, 3, 4, 5, 6].find((places) => fileSize(bytes, places) !== maximum) ?? 6

  return `${fileSize(bytes, decimals)}, the maximum for a ${mimeLabel(mime)} is ${maximum}`
}

/** The type group of an admitted mime. */
export function fileTypeOf(mime: string): AttachmentType {
  return MIME_DISPLAY[mime]?.type ?? AttachmentType.Other
}

/** The word for an admitted mime: "PDF", "PNG", … */
export function mimeLabel(mime: string): string {
  return MIME_DISPLAY[mime]?.label ?? 'File'
}

/** The SHA-256 of a file's content, hex. */
async function contentHash(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())

  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/** A picked file's content hash, read only when its type, emptiness and size let it through —
 * so a file about to be refused is never read whole — and null when it is not read or cannot be. */
export async function pickedHash(
  file: File,
  policy: HarnessAttachmentsPolicy
): Promise<string | null> {
  const mime = mimeOf(file, policy)

  if (!mime || !file.size || file.size > policy.types[mime]) {
    return null
  }

  return contentHash(file).catch(() => null)
}

/**
 * Splits a pick into the files that become chips and the ones the notice lists, in pick order:
 * duplicates by content against the chips already held, the files earlier in the same pick and the
 * files already sent — a file already there is that, whatever the limits — then type, empty, size
 * and count against the policy; a file whose bytes could not be read, last.
 */
export function pickFiles(
  policy: HarnessAttachmentsPolicy,
  held: readonly ComposerFile[],
  incoming: readonly { file: File; hash: string | null }[],
  sent: FilesInConversation
): FilePickResult {
  return incoming.reduce<FilePickResult>(
    (picked, { file, hash }) => {
      const onComposer = [...held, ...picked.admitted]
      const refusal = refusalOf(file, hash, policy, onComposer, sent)

      if (refusal || hash === null) {
        return { ...picked, refusals: [...picked.refusals, refusal ?? unreadable(file)] }
      }

      return { ...picked, admitted: [...picked.admitted, composerFile(file, hash, policy)] }
    },
    { admitted: [], refusals: [] }
  )
}

function unreadable(file: File): FileRefusal {
  return {
    name: file.name,
    reason: FileRefusalReason.Unreadable,
    detail: COMPOSER_FILE_TEXTS.unreadable,
    duplicateOfId: null,
  }
}

function refusalOf(
  file: File,
  hash: string | null,
  policy: HarnessAttachmentsPolicy,
  onComposer: readonly ComposerFile[],
  sent: FilesInConversation
): FileRefusal | null {
  const mime = mimeOf(file, policy)
  const duplicate = hash === null ? undefined : onComposer.find((held) => held.hash === hash)
  const sentBefore = hash !== null && sent.hashes.has(hash)
  const sentAs = hash === null ? null : (sent.hashes.get(hash) ?? null)
  const refuse = (
    reason: FileRefusalReason,
    detail: string,
    duplicateOfId: string | null = null
  ): FileRefusal => ({ name: file.name, reason, detail, duplicateOfId })

  if (duplicate) {
    return refuse(
      FileRefusalReason.AlreadyAttached,
      COMPOSER_FILE_TEXTS.alreadyAttached,
      duplicate.id
    )
  }

  if (sentBefore) {
    return refuse(
      FileRefusalReason.AlreadyInConversation,
      COMPOSER_FILE_TEXTS.alreadyInConversation,
      sentAs
    )
  }

  if (!mime) {
    return refuse(FileRefusalReason.UnsupportedType, unsupportedDetail(file.name))
  }

  if (!file.size) {
    return refuse(FileRefusalReason.Empty, COMPOSER_FILE_TEXTS.empty)
  }

  if (file.size > policy.types[mime]) {
    return refuse(FileRefusalReason.TooLarge, sizeOverLimit(file.size, policy.types[mime], mime))
  }

  if (onComposer.length >= policy.maxFilesPerTurn) {
    return refuse(
      FileRefusalReason.TooMany,
      `${policy.maxFilesPerTurn} files per message is the maximum`
    )
  }

  return sent.count + onComposer.length >= policy.maxFilesPerSession
    ? refuse(
        FileRefusalReason.TooMany,
        `${policy.maxFilesPerSession} files per conversation is the maximum`
      )
    : null
}

function unsupportedDetail(name: string): string {
  const extension = name.includes('.') ? name.split('.').pop()?.toUpperCase() : null

  return extension ? `${extension} files aren’t supported` : COMPOSER_FILE_TEXTS.unsupported
}

function composerFile(file: File, hash: string, policy: HarnessAttachmentsPolicy): ComposerFile {
  const mime = mimeOf(file, policy) ?? file.type

  return {
    id: crypto.randomUUID(),
    file,
    name: file.name,
    size: file.size,
    mime,
    type: fileTypeOf(mime),
    hash,
    progress: 0,
    tempFileId: null,
    error: null,
    recoverable: false,
  }
}

/** A file's chip after one more thing the uploader reported. */
export function applyUploadEvent(file: ComposerFile, event: ComposerFileUploadEvent): ComposerFile {
  switch (event.type) {
    case 'progress':
      return { ...file, progress: event.percent }
    case 'uploaded':
      return { ...file, progress: null, tempFileId: event.tempFileId }
    case 'failed':
      return { ...file, progress: null, error: event.error, recoverable: event.recoverable }
  }
}

/** A composer file as the thread shows it once sent: attached, nothing heard from the harness yet. */
export function sentFile({ name, size, hash, mime, tempFileId }: ComposerFile): HarnessSentFile {
  return {
    name,
    size,
    hash,
    mime,
    tempFileId,
    attachmentId: null,
    storeEntryUuid: null,
    state: null,
    searchable: false,
    failureReason: null,
  }
}

/** A held message's file, sent again: the same temp file, nothing heard from the harness yet. */
export function resentFile(file: HarnessSentFile): HarnessSentFile {
  return {
    ...file,
    attachmentId: null,
    storeEntryUuid: null,
    state: null,
    searchable: false,
    failureReason: null,
  }
}

/** The composer's chips a held message sent again already carries: the conversation is about to
 * hold them, so Send cannot put them in it twice. */
export function resentRepeats(
  files: readonly ComposerFile[],
  resent: readonly HarnessSentFile[]
): readonly ComposerFile[] {
  const hashes = new Set(resent.map((file) => file.hash))

  return files.filter((file) => hashes.has(file.hash))
}

export function alreadyInConversation({ name }: ComposerFile): FileRefusal {
  return {
    name,
    reason: FileRefusalReason.AlreadyInConversation,
    detail: COMPOSER_FILE_TEXTS.alreadyInConversation,
    duplicateOfId: null,
  }
}

/** How many files the conversation holds: every one it lists, and each message's file the list
 * does not show. A file just sent is named only by the read after its turn, and the list may hold
 * it already: a row no message names yet, of the same name and size — or of that name, while the
 * list carries no size — is that file, not another. */
export function conversationFileCount(
  transcript: readonly { type: string; files?: readonly HarnessSentFile[]; notRun?: boolean }[],
  listed: readonly ConversationFile[],
  removed: ReadonlySet<string>
): number {
  const sent = transcript
    .filter((entry) => !entry.notRun)
    .flatMap((entry) => entry.files ?? [])
    .filter(({ storeEntryUuid }) => !storeEntryUuid || !removed.has(storeEntryUuid))
  const listedIds = new Set(listed.map((row) => row.id))
  const namedIds = new Set(sent.flatMap(({ storeEntryUuid }) => storeEntryUuid ?? []))
  const unnamedRows = listed.filter((row) => !namedIds.has(row.id))
  const unlisted = sent.filter(({ storeEntryUuid, name, size }) =>
    storeEntryUuid
      ? !listedIds.has(storeEntryUuid)
      : !unnamedRows.some((row) => row.name === name && (row.size ?? size) === size)
  )

  return listed.length + unlisted.length
}

/** What the conversation already holds against a pick: the content this tab sent, less the files
 * since taken out, and as many files as either that or the document center counts. */
export function filesInConversation(
  transcript: readonly { type: string; files?: readonly HarnessSentFile[]; notRun?: boolean }[],
  rows: readonly ConversationFile[],
  removedIds: ReadonlySet<string>
): FilesInConversation {
  const listed = rows.filter((row) => !removedIds.has(row.id))

  return {
    hashes: sentFileHashes(transcript, removedIds),
    count: conversationFileCount(transcript, listed, removedIds),
  }
}

/** The content hashes of every file this tab sent in the conversation, each with the stored entry
 * of its latest copy, leaving out messages whose turn never ran and files since taken out of it. */
export function sentFileHashes(
  transcript: readonly { type: string; files?: readonly HarnessSentFile[]; notRun?: boolean }[],
  removed: ReadonlySet<string> = new Set()
): ReadonlyMap<string, string | null> {
  return new Map(
    transcript
      .filter((entry) => !entry.notRun)
      .flatMap((entry) => entry.files ?? [])
      .filter((file) => !(file.storeEntryUuid && removed.has(file.storeEntryUuid)))
      .flatMap((file) => (file.hash ? [[file.hash, file.storeEntryUuid] as const] : []))
  )
}

/** What the turn body carries for a message's files: their temp files. */
export function turnAttachments(
  files: readonly { tempFileId: string | null; name: string; mime: string }[]
): HarnessAttachmentRef[] {
  return files.flatMap((file) =>
    file.tempFileId
      ? [{ temp_file_id: file.tempFileId, file_name: file.name, mime_type: file.mime }]
      : []
  )
}
